import { ACTIVITY_FEATURES } from '@socialgoodsoftware/frbac';
import { ORGANIZATION_FEATURES } from '@socialgoodsoftware/frbac';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const contract = initContract();

const errorSchema = z.object({
  message: z.string(),
});

const successSchema = z.object({
  success: z.literal(true),
});

const organizationEmailDomainSchema = z.object({
  guid: z.string(),
  default: z.boolean(),
  dkim: z.array(z.string()),
  txt: z.string(),
  value: z.string(),
  verified: z.boolean(),
  txtVerified: z.boolean().optional(),
  dkimVerified: z.boolean().optional(),
  dmarcVerified: z.boolean().optional(),
  spfVerified: z.boolean().optional(),
});

const organizationSchema = z.object({
  name: z.string(),
  phone: z.string().nullable(),
  countryCode: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  clearLogsDays: z.number().nullable(),
  domains: z.array(organizationEmailDomainSchema).optional(),
});

const organizationSummarySchema = z.object({
  name: z.string(),
  precedence: z.number().int(),
  isActive: z.boolean(),
});

const organizationListSchema = z.object({
  organizations: z.array(organizationSummarySchema),
});

const getOrganizationQuerySchema = z.object({}).optional();

const organizationNameSchema = z.string().trim().min(3);

const zipSchema = z.string().regex(/^(\d{5}(-\d{4})?)?$/, 'Enter a valid ZIP code, for example 12345 or 12345-6789');

const updateOrganizationBodySchema = z.object({
  name: organizationNameSchema.optional(),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
  website: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: zipSchema.optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  clearLogsDays: z.number().int().positive().optional(),
});

const activitySeveritySchema = z.enum(['success', 'warning', 'error', 'info']);

const activityEntrySchema = z.object({
  action: z.string(),
  message: z.string(),
  type: activitySeveritySchema,
  performedBy: z.string(),
  date: z.string(),
  diff: z.array(z.unknown()).optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
});

const activityUserSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string(),
});

const getActivityQuerySchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  performedBy: z.string().optional(),
  search: z.string().optional(),
  severities: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
});

const activityListSchema = z.object({
  activities: z.array(activityEntrySchema),
  users: z.array(activityUserSchema),
  total: z.number(),
});

const exportActivityQuerySchema = z.object({
  format: z.enum(['csv', 'md', 'xlsx'], { message: 'Choose CSV, Markdown or Excel' }),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  performedBy: z.string().optional(),
  search: z.string().optional(),
  severities: z.string().optional(),
});

const switchOrganizationBodySchema = z.object({
  name: z.string().min(1),
  precedence: z.number().int(),
});

const deleteOrganizationBodySchema = z.object({}).optional();

export const organizationRoutes = contract.router(
  {
    getOrganization: {
      method: 'GET',
      metadata: { access: { feature: ORGANIZATION_FEATURES.READ.guid, sessionUsers: 'exempt' } },
      path: '/api/organization',
      query: getOrganizationQuerySchema,
      responses: {
        200: organizationSchema,
        401: errorSchema,
        403: errorSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Get the organization the signed-in session is connected to',
      description:
        'Returns the active organization for the session (profile fields, product entitlements and email domains from org Postgres). The database oid is never included, and the payload carries only the fields the SPA renders — no primary contact, no product list (the shared product directory serves that) and no created/updated stamps. Cognito group Precedence is only on listForUser / switchOrganization for the org switcher. Requires a valid SSO cookie with an active organization — otherwise 401.',
    },
    listForUser: {
      method: 'GET',
      metadata: { access: { requiresUser: true } },
      path: '/api/organizations',
      responses: {
        200: organizationListSchema,
        500: errorSchema,
      },
      summary: 'List organizations the signed-in user is a member of',
      description:
        'Returns memberships as name (Cognito Description) + precedence (Cognito group Precedence) only — no oid. isActive marks the organization the session is connected to. Client switch posts name+precedence; the server resolves the oid after verifying both fields match.',
    },
    update: {
      method: 'PUT',
      metadata: { access: { feature: ORGANIZATION_FEATURES.UPDATE.guid } },
      path: '/api/organizations',
      body: updateOrganizationBodySchema,
      responses: {
        200: successSchema,
        400: errorSchema,
        403: errorSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Update organization details',
      description:
        'Writes the organizations row in the active session organization database. The body carries no oid, so a user can only ever update the organization the SSO cookie is connected to. Every field is optional and only the keys present in the body are written, so the SPA saves one field at a time; a body that carries no writable key is 400 \'No update fields provided\' (the Zod schema accepts {}, the controller rejects it). Requires the organization:update feature — WithFeature answers 403 before anything is written (\'Permission denied\', or \'Your account has been archived for this organization.\', or \'User not found\' when the session user has no users row in that database). Changing name also renames the Cognito group Description so the org switcher and the row stay in sync; when that call fails the Postgres name is rolled back and the request is 500 \'Not able to update name at this time\'. 404 when the organizations row is missing or the write reports no change. The 200 body is the shared success flag and carries no organization payload: the SPA refetches GET /api/organization (or updates its react-query cache for a rename) and reads nothing from this response. Error bodies carry a message only.',
    },
    getActivity: {
      method: 'GET',
      metadata: { access: { feature: ACTIVITY_FEATURES.READ.guid } },
      path: '/api/organization/activity',
      query: getActivityQuerySchema,
      responses: {
        200: activityListSchema,
        403: errorSchema,
        500: errorSchema,
      },
      summary: 'Read the activity log for the session organization',
      description:
        'Returns the requested page of activity entries plus the org user roster for performedBy resolution. Filters: startDate, endDate, performedBy, search (matches action+message), severities (comma-separated success/warning/error/info). Default range is the last 30 days. The body carries only what the SPA renders — total for the pager, and no success flag, no echoed page/pageSize/totalPages (the page owns those) and no per-entry app. Exports (CSV/XLSX/Markdown) live at GET /api/organization/activity/export.',
    },
    exportActivity: {
      method: 'GET',
      metadata: { access: { feature: ACTIVITY_FEATURES.READ.guid } },
      path: '/api/organization/activity/export',
      query: exportActivityQuerySchema,
      responses: {
        200: contract.otherResponse({ contentType: 'application/octet-stream', body: contract.type<string | Uint8Array>() }),
        400: errorSchema,
        403: errorSchema,
        500: errorSchema,
      },
      summary: 'Download the activity log as a file',
      description:
        'Answers the whole filtered activity log as a downloadable file — csv, md, or xlsx, the same format vocabulary the shared ExportTable names. The SPA never fetches this endpoint: the export button builds the URL and navigates (window.location), so the browser handles the download and no React code reads the body. The same filters as GET /api/organization/activity apply (startDate, endDate, performedBy, search, severities); there is no paging — the export always carries the full range. The 200 body is the file itself with Content-Disposition attachment carrying the date-range filename (the extension names the format; the wire content type is the generic download type), and Cache-Control no-store. An unknown format is the standard Zod 400. Requires the activity:read feature via the declared access requirement.',
    },
    switchOrganization: {
      method: 'POST',
      metadata: { access: { requiresUser: true } },
      path: '/api/organization/switch',
      body: switchOrganizationBodySchema,
      responses: {
        200: successSchema,
        400: errorSchema,
        403: errorSchema,
        500: errorSchema,
      },
      summary: 'Switch the signed-in session to another organization',
      description:
        'Body is name (Cognito Description) + precedence (Cognito group Precedence), the same pair listForUser returns. The server lists the Cognito groups of the session user, takes the membership where BOTH fields match (so two organizations sharing a display name can never be confused), and only then learns the oid — which never appears on the wire in either direction. It re-validates the old session, verifies membership of the target organization again, writes a new sessions row in the target organization database, deactivates the old one, and rotates the SSO cookie to the new session id (login and switch are the same process). A selection that matches no membership is 400 \'Organization selection is invalid\'; a target the user is not an active member of is 403; a dead source session or a failed cookie write is 500. No feature gate: switching is a membership right, not a permission. The 200 body is the shared success flag and carries no organization payload — the SPA invalidates every query and reloads the shell from GET /api/organization after this call, so it reads only the status. Error bodies carry a message only.',
    },
    delete: {
      method: 'DELETE',
      metadata: { access: { feature: ORGANIZATION_FEATURES.DELETE.guid } },
      path: '/api/organization/delete',
      body: deleteOrganizationBodySchema,
      responses: {
        200: successSchema,
        403: errorSchema,
        404: errorSchema,
        500: errorSchema,
      },
      summary: 'Soft-delete the session organization',
      description:
        'Soft-deletes the organization on the active session (no oid in the body). Sets scheduledToBeRemoved=true; restore is a one-field flip.',
    },
  }
);

export type OrganizationResponse = z.infer<typeof organizationSchema>;
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;
export type OrganizationUpdate = z.infer<typeof updateOrganizationBodySchema>;
export type GetActivityQuery = z.infer<typeof getActivityQuerySchema>;
export type ActivityEntry = z.infer<typeof activityEntrySchema>;
export type ActivityListResponse = z.infer<typeof activityListSchema>;
export type ActivityUser = z.infer<typeof activityUserSchema>;
